// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Typography } from '@mui/material'
import React from 'react'
import { useTranslate } from 'react-admin'
import type { KeyFinding, KeyFindingGroup, KeyFindingSeverity } from '../utils/keyFindings'
import { useDateFormatter } from '../utils/localeDate'

/**
 * Key CGM Data Insights for Veterinary Review.
 *
 * The on-screen Summary Report block (PRD: "Key CGM Data Insights"). Presents
 * the same period statistics the generator produces as a data summary box, a
 * fixed disclaimer, and grouped finding cards (Needs Review / Monitoring
 * Context / Data Quality). Strictly data-descriptive — no diagnosis or
 * treatment recommendations. The veterinary PDF keeps its own prose layout.
 */

interface SeverityPalette {
  bar: string
  value: string
  badgeBg: string
  badgeText: string
  badgeBorder: string
}

const SEVERITY: Record<KeyFindingSeverity, SeverityPalette> = {
  // Deep orange-red — never pure red (PRD: avoid "act now" urgency)
  high: {
    bar: '#EA580C',
    value: '#C2410C',
    badgeBg: '#FFEDD5',
    badgeText: '#9A3412',
    badgeBorder: '#FED7AA',
  },
  // Amber
  medium: {
    bar: '#FBBF24',
    value: '#D97706',
    badgeBg: '#FEF3C7',
    badgeText: '#B45309',
    badgeBorder: '#FDE68A',
  },
  // Slate — visually lighter than medium/high
  info: {
    bar: '#CBD5E1',
    value: '#64748B',
    badgeBg: '#F1F5F9',
    badgeText: '#64748B',
    badgeBorder: '#E2E8F0',
  },
}

const GROUP_COLOR: Record<KeyFindingGroup, string> = {
  'needs-review': '#EA580C',
  monitoring: '#2563EB',
  'data-quality': '#64748B',
}

const LABEL_SX = {
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
}

interface KeyCGMDataInsightsProps {
  findings: KeyFinding[]
  /** Pre-built "Selected Period Data Summary" paragraph */
  summary: string
  startDate: Date
  endDate: Date
}

const CountChip: React.FC<{ count: number }> = ({ count }) => (
  <Box
    sx={{
      minWidth: 18,
      height: 18,
      px: 0.5,
      borderRadius: '9px',
      bgcolor: '#E5E7EB',
      color: '#6B7280',
      fontSize: '11px',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {count}
  </Box>
)

const GroupHeader: React.FC<{ label: string; color: string; count: number; subtitle?: string }> = ({
  label,
  color,
  count,
  subtitle,
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={{ ...LABEL_SX, color }}>{label}</Typography>
      <CountChip count={count} />
    </Box>
    {subtitle && (
      <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>{subtitle}</Typography>
    )}
  </Box>
)

const FindingCard: React.FC<{ finding: KeyFinding; compact?: boolean }> = ({
  finding,
  compact = false,
}) => {
  const translate = useTranslate()
  const sev = SEVERITY[finding.displaySeverity ?? finding.severity]

  return (
    <Box
      data-testid={`cgm-insight-${finding.id}`}
      sx={{
        position: 'relative',
        p: compact ? 1.5 : 2,
        bgcolor: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 1.5,
        borderLeft: `3px solid ${sev.bar}`,
      }}
    >
      {/* Badge + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: sev.badgeBg,
            border: `1px solid ${sev.badgeBorder}`,
            color: sev.badgeText,
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            lineHeight: 1.4,
          }}
        >
          <Box
            component="span"
            sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor' }}
          />
          {translate(`pos.key_findings.severity.${finding.displaySeverity ?? finding.severity}`)}
        </Box>
        <Typography
          sx={{
            fontSize: compact ? '12px' : '14px',
            fontWeight: 600,
            color: '#1F2937',
          }}
        >
          {finding.cardTitle ?? finding.title}
        </Typography>
      </Box>

      {/* Headline value + optional secondary value */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography
            sx={{
              fontSize: compact ? '1.5rem' : '1.875rem',
              fontWeight: 700,
              lineHeight: 1.1,
              color: sev.value,
            }}
          >
            {finding.keyValue}
            {/* A count in the slot other cards use for a glucose value reads as
                a glucose value — "311 / Readings below 79 mg/dL" was reported
                as a reading of 311 (2026-08-18). The unit word is what makes it
                a count. */}
            {finding.keyValueSuffix && (
              <Box
                component="span"
                sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', ml: 0.5 }}
              >
                {finding.keyValueSuffix}
              </Box>
            )}
          </Typography>
          {finding.keyValueLabel && (
            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#9CA3AF', mt: 0.25 }}>
              {finding.keyValueLabel}
            </Typography>
          )}
        </Box>
        {finding.subValue && (
          <Box sx={{ pb: 0.25 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#374151' }}>
              {finding.subValue}
            </Typography>
            {finding.subValueLabel && (
              <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#9CA3AF', mt: 0.25 }}>
                {finding.subValueLabel}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Meaning */}
      {finding.meaning && (
        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 1 }}>
          {finding.meaning}
        </Typography>
      )}

      {/* For Veterinary Review */}
      {finding.cardReview && (
        <Box
          sx={{
            mt: 1.5,
            p: 1,
            bgcolor: '#F8FAFC',
            border: '1px solid #F1F5F9',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: '12px', color: '#475569' }}>
            <Box component="span" sx={{ ...LABEL_SX, color: '#94A3B8', mr: 0.5 }}>
              {translate('pos.cgm_insights.review_prefix')} ·
            </Box>
            {finding.cardReview}
          </Typography>
        </Box>
      )}

      {/* See link */}
      {finding.seeLink && (
        <Box sx={{ textAlign: 'right', mt: 0.75 }}>
          <Typography component="span" sx={{ fontSize: '10px', color: '#60A5FA' }}>
            {translate('pos.cgm_insights.see_prefix')}: {finding.seeLink} →
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export const KeyCGMDataInsights: React.FC<KeyCGMDataInsightsProps> = ({
  findings,
  summary,
  startDate,
  endDate,
}) => {
  const translate = useTranslate()
  const fmtDate = useDateFormatter()

  if (!findings.length) return null

  const needsReview = findings.filter((f) => f.group === 'needs-review')
  const monitoring = findings.filter((f) => f.group === 'monitoring')
  const dataQuality = findings.filter((f) => f.group === 'data-quality')

  const dateRange = `${fmtDate(startDate, 'shortNoYear')} – ${fmtDate(endDate, 'short')}`

  return (
    <Box sx={{ mt: 3 }} data-testid="key-cgm-insights">
      {/* Module header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
            {translate('pos.cgm_insights.title')}
          </Typography>
          <Typography sx={{ ...LABEL_SX, color: '#9CA3AF', mt: 0.25 }}>
            {translate('pos.cgm_insights.subtitle')}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', textAlign: 'right' }}>
          {dateRange}
        </Typography>
      </Box>

      {/* Selected Period Data Summary */}
      {summary && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: '#FFFBEB',
            border: '1px solid #FDE68A',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ ...LABEL_SX, color: '#B45309', mb: 0.5 }}>
            {translate('pos.cgm_insights.summary_label')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#78350F', lineHeight: 1.6 }}>
            {summary}
          </Typography>
        </Box>
      )}

      {/* Disclaimer */}
      <Box
        sx={{
          mb: 2.5,
          p: 1.5,
          bgcolor: '#F8FAFC',
          border: '1px solid #E2E8F0',
          borderRadius: 1,
          display: 'flex',
          gap: 1,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8', mt: '1px', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '12px', color: '#64748B', lineHeight: 1.6 }}>
          {translate('pos.cgm_insights.disclaimer')}
        </Typography>
      </Box>

      {/* Findings: two-column (Needs Review | Monitoring + Data Quality) */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* Left — Needs Review */}
        {needsReview.length > 0 && (
          <Box sx={{ flex: { md: '0 0 63%' }, minWidth: 0 }}>
            <GroupHeader
              label={translate('pos.cgm_insights.groups.needs_review')}
              color={GROUP_COLOR['needs-review']}
              count={needsReview.length}
              subtitle={translate('pos.cgm_insights.groups.needs_review_subtitle')}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {needsReview.map((f) => (
                <FindingCard key={f.id} finding={f} />
              ))}
            </Box>
          </Box>
        )}

        {/* Right — Monitoring Context + Data Quality */}
        <Box
          sx={{
            flex: { md: '0 0 34%' },
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          {monitoring.length > 0 && (
            <Box>
              <GroupHeader
                label={translate('pos.cgm_insights.groups.monitoring')}
                color={GROUP_COLOR.monitoring}
                count={monitoring.length}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {monitoring.map((f) => (
                  <FindingCard key={f.id} finding={f} compact />
                ))}
              </Box>
            </Box>
          )}

          {dataQuality.length > 0 && (
            <Box>
              <GroupHeader
                label={translate('pos.cgm_insights.groups.data_quality')}
                color={GROUP_COLOR['data-quality']}
                count={dataQuality.length}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {dataQuality.map((f) => (
                  <FindingCard key={f.id} finding={f} compact />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
